import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import {
  IonContent,
  IonIcon,
  IonSpinner,
  ModalController,
  NavController,
  ToastController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  checkmarkCircle,
  heartOutline,
  logOutOutline,
  refreshOutline,
} from 'ionicons/icons';

import { InterestsService } from '../../pages/interests/interests.service';
import { AuthService } from '../../../core/services/auth.service';
import { UserInteresseResponseDTO } from '../../../api-clients/generated/services/AnmcoBackend/model/userInteresseResponseDTO';
import { AppRoutes } from '../../../core/enums/app-routes.enum';

@Component({
  selector: 'app-interests-onboarding-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './interests-onboarding.modal.html',
  styleUrls: ['./interests-onboarding.modal.scss'],
  imports: [IonContent, IonIcon, IonSpinner],
})
export class InterestsOnboardingModal {
  private svc = inject(InterestsService);
  private modalCtrl = inject(ModalController);
  private toastCtrl = inject(ToastController);
  private authService = inject(AuthService);
  private navCtrl = inject(NavController);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  interests: UserInteresseResponseDTO[] = [];
  selectedIds = new Set<string>();

  loading = true;
  loadError = false;
  saving = false;

  get selectionCount(): number {
    return this.selectedIds.size;
  }

  get hasSelection(): boolean {
    return this.selectedIds.size > 0;
  }

  constructor() {
    addIcons({
      'checkmark-circle': checkmarkCircle,
      'heart-outline': heartOutline,
      'log-out-outline': logOutOutline,
      'refresh-outline': refreshOutline,
    });
  }

  ionViewWillEnter() {
    this.loadInterests();
  }

  loadInterests() {
    this.loading = true;
    this.loadError = false;
    this.cdr.markForCheck();

    this.svc
      .getInteressiUtente()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (items) => {
          this.interests = items;
          this.selectedIds = new Set(
            items.filter(i => i.sottoscritto).map(i => i.id!),
          );
        },
        error: () => {
          this.loadError = true;
        },
      });
  }

  toggle(item: UserInteresseResponseDTO) {
    if (!item.id) return;
    if (this.selectedIds.has(item.id)) {
      this.selectedIds.delete(item.id);
    } else {
      this.selectedIds.add(item.id);
    }
  }

  isSelected(item: UserInteresseResponseDTO): boolean {
    return !!item.id && this.selectedIds.has(item.id);
  }

  save() {
    this.saving = true;
    this.cdr.markForCheck();

    this.svc
      .aggiornaInteressi([...this.selectedIds])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.modalCtrl.dismiss(null, 'confirm');
        },
        error: async () => {
          const toast = await this.toastCtrl.create({
            message: 'Errore nel salvataggio. Riprova.',
            color: 'danger',
            position: 'bottom',
            duration: 4000,
          });
          await toast.present();
        },
      });
  }

  async exitApp() {
    await this.authService.logout();
    await this.modalCtrl.dismiss(null, 'logout');
    this.navCtrl.navigateRoot(`/${AppRoutes.AUTH}/${AppRoutes.LOGIN}`);
  }
}
